// @ts-nocheck
import useFAQ from '../../data/queryHooks/useFAQ';
import { bool, string } from 'prop-types';
import DataFetchingWrapper from '../shared/DataFetchingWrapper';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { grey } from '@mui/material/colors';
import { useState, useEffect } from 'react';
import CopyLinkButton from './CopyLinkButton';
import FAQSkeleton from './FAQSkeleton';
import { contentfulOptions } from '../../scripts/miscConstants';
import { sendGAEvent } from '../../utils/googleAnalytics';
import GridWrapper from '../shared/GridWrapper';

export const FaqContent = ({ apiEnv, product }) => {
	const { data } = useFAQ(apiEnv, product);
	const [expanded, setExpanded] = useState(false);

	const handleChangeExpanded = panel => (_event, isExpanded) => {
		setExpanded(isExpanded ? panel : false);
	};
	// If a specific question was included in the url, scroll to that question and expand its accordion.
	useEffect(() => {
		const { hash } = window.location;

		if (hash) {
			setExpanded(hash); // Expand the question specified in the url.
			const scrollElement = document.getElementById(hash);

			if (scrollElement) {
				// Scroll to the question specified in the url.
				scrollElement.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				});
			}
		}
	}, []);

	return (
		<>
			{data.faq.faqCategories.map(faqCategory => (
				<GridWrapper>
					<Box key={faqCategory.category}>
						<Typography
							variant="h5"
							sx={{ fontWeight: 'fontWeightSemiBold' }}
							ml={2}
							mb={3}
						>
							{faqCategory.category}
						</Typography>
						{faqCategory.questionAnswers.map(questionAnswer => {
							const id = `#${questionAnswer.urlTag}`;
							const questionId = `${questionAnswer.questionId}`;
							const linkToCopy = `${window.location.origin}${window.location.pathname}${id}`;
							const isExpanded = expanded === id;

							return (
								<Box key={questionAnswer.question} mb={1}>
									<Accordion
										expanded={isExpanded}
										onChange={handleChangeExpanded(id)}
										onClick={() =>
											sendGAEvent(`faq_${questionId}`, {
												data_question_id: questionId,
											})
										}
										id={id}
										sx={{
											boxShadow: 'none',
											'&.MuiAccordion-root.Mui-expanded': {
												boxShadow: `0px 3px 5px -1px ${grey[300]}`,
												backgroundColor: grey[50],
											},
										}}
									>
										<AccordionSummary expandIcon={<ExpandMoreIcon />}>
											<Typography variant="body3" mr={2}>
												{questionAnswer.question}
											</Typography>
										</AccordionSummary>
										<AccordionDetails>
											<Typography
												variant="body3"
												component="div"
												color="text.secondary"
											>
												{documentToReactComponents(
													questionAnswer.answer,
													contentfulOptions
												)}
											</Typography>
											<CopyLinkButton linkToCopy={linkToCopy} />
										</AccordionDetails>
									</Accordion>
								</Box>
							);
						})}
						<Divider />
					</Box>
				</GridWrapper>
			))}
			{data.faq.disclaimer && (
				<Box mt={5} ml={2}>
					<Typography variant="h6">Disclaimers</Typography>
					<Typography
						variant="body1"
						color="text.secondary"
						component="section"
					>
						{documentToReactComponents(data.faq.disclaimer, contentfulOptions)}
					</Typography>
				</Box>
			)}
		</>
	);
};

FaqContent.propTypes = {
	/**
	 * Used primarily to determine the correct API endpoint. These should adhere to
	 * the [CAST guidelines](https://dev.azure.com/ashcompanies/CAST/_wiki/wikis/CAST.wiki/1758/Standardized-Cross-Team-Variables).
	 * ```
	 * dev
	 * ```
	 */
	apiEnv: string.isRequired,
	/*
	 * The name of the product consuming this component, such as ASHCare, Empowered Decisions, etc.
	 */
	product: string,
};

const FAQ = ({ apiEnv, product, hasOwnProvider }) => (
	<Box>
		{/* Put the title outside of the DataFetchingWrapper so it still shows while the data is loading */}
		<Typography mb={8} textAlign="center" variant="h3" component="h4">
			Frequently Asked Questions
		</Typography>
		<DataFetchingWrapper
			hasOwnProvider={hasOwnProvider}
			SuspenseFallBack={<FAQSkeleton />}
		>
			<FaqContent apiEnv={apiEnv} product={product} />
		</DataFetchingWrapper>
	</Box>
);

FAQ.propTypes = {
	/**
	 * Used primarily to determine the correct API endpoint. These should adhere to
	 * the [CAST guidelines](https://dev.azure.com/ashcompanies/CAST/_wiki/wikis/CAST.wiki/1758/Standardized-Cross-Team-Variables).
	 * ```
	 * dev
	 * ```
	 */
	apiEnv: string.isRequired,
	/*
	 * The name of the product consuming this component, such as ASHCare, Empowered Decisions, etc.
	 */
	product: string,
	/*
	 * If the consuming app is using react-query and has its own QueryClientProvider tags, this should
	 *  be set to true. Any consuming apps not using react-query should have this set to false.
	 */
	hasOwnProvider: bool,
};

export default FAQ;
